import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  GenericResp,
  GET_ALL_DICTATIONS_RESP,
  GetAllPromptsResponse,
} from '../models';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AudioDictationService {
  constructor(private http: HttpClient) {}

  async getAudioDictations(
    client_id: string,
    practice_id: string,
    page: number = 1,
    limit: number = 10,
    orderBy: {},
    search: string,
    searchCriteria: string,
    filterByProvider: string,
    is_archived: boolean
  ): Promise<GET_ALL_DICTATIONS_RESP> {
    return this.http
      .get<GET_ALL_DICTATIONS_RESP>(
        `${environment.AUDIO_DICTATION}/client/${client_id}/practice/${practice_id}`,
        {
          params: {
            page: `${page}`,
            limit: `${limit}`,
            orderBy: JSON.stringify(orderBy),
            search,
            searchCriteria,
            filterByProvider,
            is_archived: `${!!is_archived}`,
          },
        }
      )
      .toPromise();
  }

  async getMissingAudioDictations(
    client_id: string,
    practice_id: string,
    page: number = 1,
    limit: number = 10,
    search: string,
    searchCriteria: string,
    filterByProvider: string,
    is_archived: boolean
  ): Promise<any> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .get<any>(
        `${environment.AUDIO_DICTATION}/client/${client_id}/practice/${practice_id}/missing-files`,
        {
          headers: {
            Authorization: `${user.stsTokenManager.accessToken}`,
          },
          params: {
            page: `${page}`,
            limit: `${limit}`,
            search,
            searchCriteria,
            filterByProvider,
            is_archived: 'false',
          },
        }
      )
      .toPromise();
  }

  async sendMissingAudioDictationsNotifications(
    client_id: string,
    practice_id: string
  ): Promise<any> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .post<any>(
        `${environment.AUDIO_DICTATION}/client/${client_id}/practice/${practice_id}/send-missing-notification`,
        {
          headers: {
            Authorization: `${user.stsTokenManager.accessToken}`,
          },
        }
      )
      .toPromise();
  }

  async deleteAudioDictation(audio_id: string): Promise<GenericResp> {
    return await this.http
      .delete<GenericResp>(`${environment.AUDIO_DICTATION}/${audio_id}`)
      .toPromise();
  }

  async deleteUnsucessfulAudios(practice_id: string): Promise<GenericResp> {
    return await this.http
      .delete<GenericResp>(
        `${environment.AUDIO_DICTATION}/practice/${practice_id}/missing-dictations`
      )
      .toPromise();
  }

  async updateAudioDictation(
    audio_id: string,
    is_archived: boolean
  ): Promise<GenericResp> {
    return await this.http
      .put<GenericResp>(
        `${environment.AUDIO_DICTATION}/${audio_id}/archive/${is_archived}`,
        null
      )
      .toPromise();
  }

  async updateAudioTranscription(
    id: string,
    transcription_text: string
  ): Promise<GenericResp> {
    const body = { transcription_text };
    return await this.http
      .put<GenericResp>(`${environment.AUDIO_DICTATION}/${id}`, body)
      .toPromise();
  }
  async getTranscription(file: any): Promise<any> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .post<any>(`${environment.BASE_URL}/ai/web/transcription`, file, {
        headers: {
          Authorization: `${user.stsTokenManager.accessToken}`,
        },
      })
      .toPromise();
  }

  getMultipleFilesTranscription(files: FormData): Observable<HttpEvent<any>> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http.post<any>(
      `${environment.BASE_URL}/ai/web/transcriptions`,
      files,
      {
        headers: {
          Authorization: `${user.stsTokenManager.accessToken}`,
        },
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  async getDoctorNotes(transcription: any, prompt?: string): Promise<any> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .post<any>(
        `${environment.BASE_URL}/ai/web/doctor-notes`,
        { transcription, prompt },
        {
          headers: {
            Authorization: `${user.stsTokenManager.accessToken}`,
          },
        }
      )
      .toPromise();
  }

  async mergeTranscription(dictation: any): Promise<any> {
  const user = JSON.parse(localStorage.getItem('user'));
    const currentDate = new Date().toISOString();

      const url = `${environment.BASE_URL}/dictation/audios/web/merge-transcription?date=${encodeURIComponent(currentDate)}`;

  return this.http
    .post<any>(
      url,
      {
        id: dictation.id,
        patient_id: dictation.patient_id,
        dictation_number: dictation.dictation_number,
      },
      {
        headers: {
          Authorization: `${user.stsTokenManager.accessToken}`,
        },
      }
    )
    .toPromise();
}
  
    async getDoctorNotesBySplitPrompts(transcription: any): Promise<any> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .post<any>(
        `${environment.BASE_URL}/ai/web/split-prompts/doctor-notes`,
        { transcription },
        {
          headers: {
            Authorization: `${user.stsTokenManager.accessToken}`,
          },
        }
      )
      .toPromise();
  }

  async getSixMonthsOrtMasters(
    patientId: string
  ): Promise<{
    code: number;
    data: { ortMasters: any[]; doctorNotes: any[]; mriReports: any[]; ctReports: any[]; operativeReports: any[] };
  }> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .get<any>(`${environment.BASE_URL}/ai/automation/ort-masters/patient/${patientId}`, {
        headers: {
          Authorization: `${user.stsTokenManager.accessToken}`,
        },
      })
      .toPromise();
  }

  async getAllPrompts(): Promise<GetAllPromptsResponse> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .get<GetAllPromptsResponse>(`${environment.BASE_URL}/ai/prompt?type=1`, {
        headers: {
          Authorization: `${user.stsTokenManager.accessToken}`,
        },
      })
      .toPromise();
  }

  async publishPrompt(content: string, type: number): Promise<GenericResp> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .post<GenericResp>(
        `${environment.BASE_URL}/ai/prompt`,
        { content, type },
        { headers: { Authorization: `${user.stsTokenManager.accessToken}` } }
      )
      .toPromise();
  }

  async getPresignedUrl(bucket: string, key: string): Promise<string> {
    const user = JSON.parse(localStorage.getItem('user'));
    const resp = await this.http
      .get<any>(`${environment.AUDIO_DICTATION}/presigned-url`, {
        headers: { Authorization: `${user.stsTokenManager.accessToken}` },
        params: { bucket, key },
      })
      .toPromise();
    return resp.data.url;
  }

  async getBillingAnalysis(transcription: string, transcriptionId?: string): Promise<any> {
    const user = JSON.parse(localStorage.getItem('user'));
    const body: any = { transcription };
    if (transcriptionId) body.transcription_id = transcriptionId;
    return this.http
      .post<any>(
        `${environment.BASE_URL}/ai/web/billing-analysis`,
        body,
        {
          headers: {
            Authorization: `${user.stsTokenManager.accessToken}`,
          },
        }
      )
      .toPromise();
  }

  getTodaysDictationsByPatient(patientId: string, providerId?: string): Promise<{ data: any[] }> {
    let url = `${environment.AUDIO_DICTATION}/patient/${patientId}/today`;
    if (providerId) url += `?provider_id=${encodeURIComponent(providerId)}`;
    return this.http.get<{ data: any[] }>(url).toPromise();
  }

  // Paginated across ALL of a patient's dictations (not just today's) — feeds the ORT Masters
  // tab's Files panel. See audioDictationService.getDictationsPaginatedForPatient.
  getDictationsForPatient(
    patientId: string,
    page: number,
    limit: number,
    providerId?: string
  ): Promise<{ code: number; data: { data: any[]; total: number; currentPage: number; previousPage: number | null; nextPage: number | null; limit: number } }> {
    let url = `${environment.AUDIO_DICTATION}/patient/${patientId}/all?page=${page}&limit=${limit}`;
    if (providerId) url += `&provider_id=${encodeURIComponent(providerId)}`;
    return this.http.get<any>(url).toPromise();
  }

  getAiProviders(practiceId?: string): Promise<{ code: number; data: { provider_id: string; description: string }[] }> {
    const url = practiceId
      ? `${environment.BASE_URL}/provider/ai-providers/${encodeURIComponent(practiceId)}`
      : `${environment.BASE_URL}/provider/ai-providers`;
    return this.http.get<any>(url).toPromise();
  }

  // Paginated across ALL of a patient's dictations (not just today's) — feeds the ORT Masters
  // tab's Action Items panel. See aiService.getActionItemsPaginatedForPatient.
  getActionItemsForPatient(
    patientId: string,
    page: number,
    limit: number,
    providerId?: string
  ): Promise<{ code: number; data: { data: any[]; total: number; currentPage: number; previousPage: number | null; nextPage: number | null; limit: number } }> {
    const user = JSON.parse(localStorage.getItem('user'));
    let url = `${environment.BASE_URL}/ai/automation/action-items/patient/${patientId}?page=${page}&limit=${limit}`;
    if (providerId) url += `&provider_id=${encodeURIComponent(providerId)}`;
    return this.http
      .get<any>(url, { headers: { Authorization: `${user.stsTokenManager.accessToken}` } })
      .toPromise();
  }

  // Resolves last six months of ORT Masters and Doctor Notes for the patient behind a given
  // action item. See aiService.getOrtMastersByEncounterOrActionItem.
  lookupOrtMastersByActionItem(
    actionItemId: string
  ): Promise<{
    code: number;
    data: { ortMasters: any[]; doctorNotes: any[]; mriReports: any[]; ctReports: any[]; operativeReports: any[] };
  }> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .post<any>(
        `${environment.BASE_URL}/ai/automation/ort-masters/lookup`,
        { action_item_id: actionItemId },
        { headers: { Authorization: `${user.stsTokenManager.accessToken}` } }
      )
      .toPromise();
  }

  // Doctor Notes JSON section for a dictation — fallback content when an action item has no
  // ORT Master. See aiService.getDoctorNotesByDictation.
  getDoctorNotesByDictation(dictationId: string): Promise<{ code: number; data: { doctor_notes: any } }> {
    const user = JSON.parse(localStorage.getItem('user'));
    return this.http
      .get<any>(`${environment.BASE_URL}/ai/doctor-notes/dictation/${dictationId}`, {
        headers: { Authorization: `${user.stsTokenManager.accessToken}` },
      })
      .toPromise();
  }
}
